import { useState } from 'react';
import { FaFacebookF, FaTwitter } from 'react-icons/fa';
import { AiFillInstagram } from "react-icons/ai";
import { IoLogoYoutube } from "react-icons/io";
import logo from '../assets/fala.png'
import supabase from '../config/supabaseConfig';
import { CONTACT_EMAIL, CONTACT_EMAIL_HREF, SOCIAL_LINKS } from '../config/contactInfo';
import AlertToast, { AlertToastType } from './ui/AlertToast';

type SubscribeStatus = 'idle' | 'sending' | 'success' | 'duplicate' | 'error';

const Footer = () => {
    const [email, setEmail] = useState('');
    const [subscribeStatus, setSubscribeStatus] = useState<SubscribeStatus>('idle');
    const [alert, setAlert] = useState<{ type: AlertToastType; message: string } | null>(null);

    const handleSubscribe = async () => {
        const trimmedEmail = email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setSubscribeStatus('error');
            setAlert({ type: 'error', message: 'Veuillez entrer une adresse email valide.' });
            return;
        }

        setSubscribeStatus('sending');

        const { error } = await supabase
            .from('newsletter_subscribers')
            .insert({ email: trimmedEmail });

        if (error) {
            if (error.code === '23505') {
                setSubscribeStatus('duplicate');
                setAlert({ type: 'error', message: 'Vous êtes déjà abonné(e) à la newsletter !' });
            } else {
                setSubscribeStatus('error');
                setAlert({ type: 'error', message: 'Une erreur est survenue. Veuillez réessayer.' });
            }
            return;
        }

        setEmail('');
        setSubscribeStatus('success');
        setAlert({ type: 'success', message: 'Merci ! Vous êtes maintenant abonné(e) à la newsletter.' });

        // Fire-and-forget welcome email — capture already succeeded above either way.
        supabase.functions
            .invoke('send-email', { body: { type: 'newsletter_welcome', email: trimmedEmail } })
            .catch((err) => console.error('Welcome email failed to send:', err));
    };

    const aboutLinks = [
        { text: "À propos de Falashop", href: "/about-falashop" },
        { text: "Nous recrutons !", href: "/we-are-hiring" },
        { text: "Conditions générales", href: "/terms-of-service" },
        { text: "Politique de confidentialité", href: "/privacy-policy" },
        { text: "Politique de facturation", href: "/billing-policy" },
        { text: "Politique de violation de droits d'auteur", href: "/copyright-violation-policy" },
        { text: "FAQ", href: "/faq" },
        { text: "Blog", href: "/blog" }
    ];

    const supportLinks = [
        { text: CONTACT_EMAIL, href: CONTACT_EMAIL_HREF },
        { text: "Conseils de sécurité", href: "/security-tips" },
        { text: "Contactez-nous", href: "/contact-us" }
    ];

    const socialLinks = [
        { icon: <FaFacebookF />, href: SOCIAL_LINKS.facebook },
        { icon: <FaTwitter />, href: SOCIAL_LINKS.twitter },
        { icon: <AiFillInstagram />, href: SOCIAL_LINKS.instagram },
        { icon: <IoLogoYoutube />, href: SOCIAL_LINKS.youtube }
    ];

    return (
        <footer className="bg-gradient-to-r from-primary-default to-primary-secondary text-primary-contrast pt-16 px-4 sm:px-8 transition-all duration-300 ease-in-out">
            {alert && (
                <AlertToast type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
            )}
            <div className="container mx-auto flex flex-col lg:flex-row text-center md:text-left lg:text-left gap-8 justify-between px-4">
                {/* Logo et Nom de l'entreprise */}
                <div className="flex flex-col items-center mb-4 lg:mb-0 transform hover:scale-105 transition-transform duration-300">
                    <img src={logo} alt="Logo de l'entreprise" className="h-10 w-auto mr-2" />
                    <div className="container max-w-[20rem] text-center mt-4">
                        <p className="text-sm">
                            Falashop est votre plateforme de confiance pour acheter et vendre en toute sécurité.
                            Rejoignez notre communauté et découvrez des milliers d'annonces de qualité.
                        </p>
                    </div>
                </div>

                {/* Liens du pied de page */}
                <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-8">
                    {/* À propos */}
                    <div className="mb-4 lg:mb-0">
                        <h3 className="text-lg font-semibold mb-2">À propos</h3>
                        <ul className="list-none flex flex-col gap-2">
                            {aboutLinks.map((link, index) => (
                                <li key={index} className="transform hover:translate-x-1 transition-transform duration-200">
                                    <a href={link.href} className="hover:text-primary-default transition-colors duration-200">
                                        {link.text}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Support */}
                    <div className="mb-4 lg:mb-0">
                        <h3 className="text-lg font-semibold mb-2">Support</h3>
                        <ul className="list-none flex flex-col gap-2">
                            {supportLinks.map((link, index) => (
                                <li key={index} className="transform hover:translate-x-1 transition-transform duration-200">
                                    <a href={link.href} className="hover:text-primary-default transition-colors duration-200">
                                        {link.text}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Abonnement */}
                <div className="mb-4 lg:mb-0 w-full lg:w-auto">
                    <h3 className="text-lg font-semibold mb-2">S'abonner</h3>
                    <div className="flex flex-col sm:flex-row items-center gap-2">
                        <input
                            type="email"
                            placeholder="Entrez votre email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (subscribeStatus !== 'idle' && subscribeStatus !== 'sending') {
                                    setSubscribeStatus('idle');
                                }
                            }}
                            className="w-full sm:w-auto bg-surface-default text-content-tertiary px-4 py-2 rounded-md sm:rounded-l-md sm:rounded-r-none focus:outline-none focus:ring-2 focus:ring-primary-default transition-all duration-200"
                        />
                        <button
                            onClick={handleSubscribe}
                            disabled={subscribeStatus === 'sending'}
                            className="w-full sm:w-auto bg-primary-default text-light-default px-4 py-2 rounded-md sm:rounded-l-none sm:rounded-r-md hover:bg-primary-secondary focus:outline-none focus:ring-2 focus:ring-primary-default transform hover:scale-105 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {subscribeStatus === 'sending' ? 'Envoi...' : "S'abonner"}
                        </button>
                    </div>

                    {/* Icônes des réseaux sociaux */}
                    <div className="flex justify-center lg:justify-start space-x-4 mt-8">
                        {socialLinks.map((link, index) => (
                            <a
                                key={index}
                                href={link.href}
                                className="text-primary-contrast hover:text-primary-default text-2xl transform hover:scale-110 transition-all duration-200"
                            >
                                {link.icon}
                            </a>
                        ))}
                    </div>
                </div>
            </div>

            {/* Boutons de téléchargement */}
            {/* <div className="flex justify-center mt-16">
                <div className="flex items-center border rounded-lg px-4 py-2 w-52 mx-2">
                    <img src="https://cdn-icons-png.flaticon.com/512/888/888857.png" className="w-7 md:w-8" alt="Google Play" />
                    <div className="text-left ml-3">
                        <p className='text-xs text-light-tertiary'>Télécharger sur</p>
                        <p className="text-sm md:text-base">Google Play Store</p>
                    </div>
                </div>
                <div className="flex items-center border rounded-lg px-4 py-2 w-44 mx-2">
                    <img src="https://cdn-icons-png.flaticon.com/512/888/888841.png" className="w-7 md:w-8" alt="App Store" />
                    <div className="text-left ml-3">
                        <p className='text-xs text-light-tertiary'>Télécharger sur</p>
                        <p className="text-sm md:text-base">App Store</p>
                    </div>
                </div>
            </div> */}

            <hr className="h-px my-6 bg-solid-200 border-none dark:bg-solid-300" />

            <div className='pb-4'>
                <p className="text-center text-content-tertiary">© Falashop 2026 - Tous droits réservés</p>
            </div>
        </footer>
    );
};

export default Footer;
